import {
	type SendingAnomalyListData,
	sendingAnomalyGet,
	sendingAnomalyGetRecipient,
	sendingAnomalyList,
	sendingAnomalyListResidences
} from '@repo/sdk/gen'
import { queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/query/query-keys'

// What the console of the sendings shows holds no student: the counters, the
// list and the detail of an anomaly are read again when the window gets the
// focus, because a failure that opened a minute ago has to be seen now. The one
// student datum is the address an email was refused at: it is read apart, once
// per opening, because every fetch of it is a `personal_data.viewed` fact
// (decision D37).

/** Every residence with its open anomalies. */
export function sendingResidencesQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.sendingAnomalies.residences(),
		queryFn: ({ signal }) => sendingAnomalyListResidences({ signal }),
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}

/** The anomalies, the open ones first. */
export function sendingAnomaliesQueryOptions(
	body: SendingAnomalyListData['body']
) {
	return queryOptions({
		queryKey: makeQueryKeys.sendingAnomalies.index(body),
		queryFn: ({ signal }) => sendingAnomalyList({ body, signal }),
		placeholderData: (prev) => prev,
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}

/** One anomaly with the application it belongs to. */
export function sendingAnomalyQueryOptions(anomalyId: string) {
	return queryOptions({
		queryKey: makeQueryKeys.sendingAnomalies.detail(anomalyId),
		queryFn: async ({ signal }) =>
			(await sendingAnomalyGet({ body: { data: { anomalyId } }, signal }))
				.data,
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}

/** The address an email to a student was refused at; read once, never again by itself. */
export function sendingAnomalyRecipientQueryOptions(anomalyId: string) {
	return queryOptions({
		queryKey: makeQueryKeys.sendingRecipients.detail(anomalyId),
		queryFn: async ({ signal }) =>
			(
				await sendingAnomalyGetRecipient({
					body: { data: { anomalyId } },
					signal
				})
			).data
	})
}
